import { all, call, put, spawn, takeLatest, select } from 'redux-saga/effects';
import { RootState } from 'types';

import { inboxPageActions as actions } from '.';

import api from 'app/api';
import { getBusinessIdFromToken } from 'utils/getBusinessId';
import { successModal, errorModal } from 'utils/showSuccessErrorModal';

/* Clients APIs */
export function* getClientList(action) {
  try {
    const state1 = yield select((state: RootState) => state.authPage);
    const url = '/clients';
    if (action?.payload?.limit) {
      const urlWithLimit = `${url}?limit=${action.payload.limit}`;
      const responseWithLimit = yield call(api.get, urlWithLimit);
      yield put(
        actions.fetchClientListSuccess({
          data: responseWithLimit.data.data,
          usernameMasking: state1?.permission?.contactNumberMasking,
        }),
      );
    }
    const response = yield call(api.get, url);
    yield put(
      actions.fetchClientListSuccess({
        data: response.data.data,
        usernameMasking: state1?.permission?.contactNumberMasking,
      }),
    );
  } catch (error: any) {
    yield put(actions.fetchClientListFailure(error?.response?.data));
  }
}

export function* addContacts(action) {
  try {
    const response = yield call(api.post, `/clients`, action.payload);
    yield put(actions.addContactsSuccess(response.data.data));
    successModal(response?.data?.message || 'Contact updated successfully');
  } catch (err: any) {
    yield put(actions.addContactsFailure(err?.response?.data));
    errorModal(err?.response?.data?.errorMessage || 'Failed to update contact');
  }
}

export function* updateClientAttributesAndTags(action) {
  try {
    const response = yield call(
      api.put,
      `/clients/attributes-and-tags`,
      action.payload,
    );
    yield put(actions.updateClientAttributesAndTagsSuccess(response.data.data));
    successModal('Updated successfully');
  } catch (err: any) {
    yield put(
      actions.updateClientAttributesAndTagsFailure(err?.response?.data),
    );
    errorModal(err?.response?.data?.errorMessage || 'Failed to update');
  }
}

export function* toggleChat(action) {
  const { clientWaNumber, isOpen } = action.payload;
  try {
    yield call(api.put, `/clients/chat/toggle/${clientWaNumber}`, { isOpen });
    yield put(actions.toggleClientSuccess({ clientWaNumber, result: isOpen }));
    successModal(isOpen ? 'Chat opened' : 'Chat closed');
  } catch (error: any) {
    yield put(actions.toggleClientFailure(error?.response?.data));
    errorModal(error?.response?.data?.errorMessage || 'Failed to toggle chat');
  }
}

export function* toggleBotForClient(action) {
  const { clientWaNumber, isBotReply } = action.payload;
  try {
    yield call(api.put, `/clients/bot/toggle/${clientWaNumber}`, {
      isBotReply,
    });
    yield put(
      actions.toggleBotForClientSuccess({ clientWaNumber, result: isBotReply }),
    );
    successModal(isBotReply ? 'Bot enabled' : 'Bot disabled');
  } catch (error: any) {
    yield put(actions.toggleBotForClientFailure(error?.response?.data));
    errorModal(error?.response?.data?.errorMessage || 'Failed to toggle bot');
  }
}
/* Messages APIs */
export function* getMessages(action) {
  const { clientWaNumber, isFromMessages } = action.payload;
  try {
    if (isFromMessages) {
      const response = yield call(api.get, `/messages/${clientWaNumber}`);
      yield put(actions.fetchMessagesSuccess(response.data.data));
    } else {
      const responseLimit = yield call(
        api.get,
        `/messages/${clientWaNumber}?limit=100`,
      );
      yield put(actions.fetchMessagesSuccess(responseLimit.data.data));
      if (responseLimit.data.data.messages.length >= 100) {
        const response = yield call(api.get, `/messages/${clientWaNumber}`);
        yield put(actions.fetchMessagesSuccess(response.data.data));
      }
    }
  } catch (error: any) {
    yield put(actions.fetchMessagesFailure(error?.response?.data));
  }
}

export function* sendMessages(action) {
  try {
    const response = yield call(api.post, `/messages/send`, {
      messages: action.payload,
    });
    yield put(actions.sendMessagesFailure(response.data.data));
  } catch (err: any) {
    yield put(actions.sendMessagesFailure(err?.response?.data));
  }
}

export function* getSingleClient(action) {
  const { clientWaNumber } = action.payload;
  try {
    const response = yield call(api.get, `/clients/${clientWaNumber}`);
    const client = { ...response.data.data, messages: [], isLastMessage: true };
    yield put(actions.setSelectedClient(client));
    yield put(actions.fetchMessagesStart({ clientWaNumber }));
  } catch (_error: any) {
    yield put(actions.selectClientByUrl({ clientWaNumber }));
  }
}

export function* addTemplateLink(action) {
  try {
    yield call(api.post, `/templates/media-link`, { ...action.payload });
  } catch (error: any) {
    console.error('addTemplateLink error:', error?.response?.data);
  }
}

export function* bulkAddTemplateMediaLink(action) {
  try {
    yield call(api.post, `/templates/bulk-media-link`, { ...action.payload });
  } catch (error: any) {
    console.error('bulkAddTemplateMediaLink error:', error?.response?.data);
  }
}

export function* getTemplateLink() {
  try {
    const response = yield call(api.get, `/templates/media-link`);
    yield put(actions.getTemplateMediaLinkSuccess(response.data.data));
  } catch (error: any) {
    yield put(actions.getTemplateMediaLinkFailure(error?.response?.data));
    errorModal(
      error?.response?.data?.errorMessage || 'Failed to fetch template links',
    );
  }
}

export function* getTemplates(action) {
  const refresh = action.payload?.refresh;
  const businessId = getBusinessIdFromToken();
  try {
    const response = yield call(
      api.get,
      refresh ? '/templates?refresh=true' : '/templates',
    );
    const templates = response.data.data;

    // Persist templates for offline usage without blocking the latest payload
    try {
      localStorage.setItem(
        `templates_${businessId}`,
        JSON.stringify(templates),
      );
    } catch (storageError) {
      console.warn('Unable to cache templates in localStorage:', storageError);
    }

    yield put(actions.getTemplatesSuccess(templates));
  } catch (error: any) {
    yield put(
      actions.getTemplatesSuccess(
        JSON.parse(localStorage.getItem(`templates_${businessId}`) || '[]'),
      ),
    );
    if (
      error?.response?.data &&
      !JSON.stringify(error.response.data).includes('ETIMEDOUT') &&
      !JSON.stringify(error.response.data).includes('ECONNABORTED')
    ) {
      yield put(actions.getTemplatesFailure(error?.response?.data));
      errorModal(
        error?.response?.data?.errorMessage || 'Failed to fetch templates',
      );
    }
  }
}

export function* getFlows() {
  const businessId = getBusinessIdFromToken();
  try {
    const response = yield call(api.get, `/flows`);
    localStorage.setItem(
      `flows_${businessId}`,
      JSON.stringify(response.data.data),
    );
    yield put(actions.getFlowsSuccess(response.data.data));
  } catch (_error: any) {
    yield put(
      actions.getFlowsSuccess(
        JSON.parse(localStorage.getItem(`flows_${businessId}`) || '[]'),
      ),
    );
  }
}

function* watchFetchClientList() {
  yield takeLatest(actions.fetchClientListStart.type, getClientList);
}

function* watchFetchMessages() {
  yield takeLatest(actions.fetchMessagesStart.type, getMessages);
}

function* watchToggleChat() {
  yield takeLatest(actions.toggleClientStart.type, toggleChat);
}
function* watchToggleBotForClient() {
  yield takeLatest(actions.toggleBotForClientStart.type, toggleBotForClient);
}

function* watchAddContacts() {
  yield takeLatest(actions.addContactsStart.type, addContacts);
}

function* watchUpdateClientAttributesAndTags() {
  yield takeLatest(
    actions.updateClientAttributesAndTagsStart.type,
    updateClientAttributesAndTags,
  );
}

function* watchSendMessages() {
  yield takeLatest(actions.sendMessagesStart.type, sendMessages);
}

function* watchFetchSingleClient() {
  yield takeLatest(actions.fetchSingleClientStart.type, getSingleClient);
}

function* watchGetTemplateMediaLink() {
  yield takeLatest(actions.getTemplateMediaLinkStart.type, getTemplateLink);
}

function* watchBulkAddTemplateMediaLink() {
  yield takeLatest(
    actions.bulkAddTemplateMediaLink.type,
    bulkAddTemplateMediaLink,
  );
}

function* watchAddTemplateMediaLink() {
  yield takeLatest(actions.addTemplateMediaLink.type, addTemplateLink);
}

function* watchGetTemplates() {
  yield takeLatest(actions.getTemplatesStart.type, getTemplates);
}

function* watchGetFlows() {
  yield takeLatest(actions.getFlowsStart.type, getFlows);
}

export function* inboxPageSaga() {
  const sagas = [
    watchFetchClientList,
    watchFetchMessages,
    watchToggleChat,
    watchToggleBotForClient,
    watchAddContacts,
    watchUpdateClientAttributesAndTags,
    watchSendMessages,
    watchFetchSingleClient,
    watchGetTemplateMediaLink,
    watchBulkAddTemplateMediaLink,
    watchAddTemplateMediaLink,
    watchGetTemplates,
    watchGetFlows,
  ];

  yield all(
    sagas.map(saga =>
      spawn(function* () {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            console.error('Saga error in inboxPagaSaga:', e);
          }
        }
      }),
    ),
  );
}
